import type { TPlanningGroup } from '@sediwork/shared/types/planningGroup';
import type { TFacilityId, TFacilityIdsByType } from '../types/facility';
import {
  EUserFacilityRelationType,
  type TFacilitiesByType,
  type TUserFacilityRelation,
  type TUserWithFacilities,
} from '../types/user';

function createEmptyFacilityBuckets(): TFacilitiesByType {
  return {
    [EUserFacilityRelationType.ADMINISTRATE]: [],
    [EUserFacilityRelationType.COORDINATE]: [],
    [EUserFacilityRelationType.READ]: [],
  };
}

function createEmptyFacilityIdBuckets(): TFacilityIdsByType {
  return {
    [EUserFacilityRelationType.ADMINISTRATE]: new Set<TFacilityId>(),
    [EUserFacilityRelationType.COORDINATE]: new Set<TFacilityId>(),
    [EUserFacilityRelationType.READ]: new Set<TFacilityId>(),
  };
}

export function groupFacilitiesByType(
  facilities: ReadonlyArray<TUserFacilityRelation> | undefined,
): TFacilitiesByType {
  const buckets = createEmptyFacilityBuckets();
  facilities?.forEach((relation) => {
    if (relation?.type && buckets[relation.type]) {
      buckets[relation.type].push(relation);
    }
  });
  return buckets;
}

export function groupFacilityIdsByType(
  facilities: ReadonlyArray<TUserFacilityRelation> | undefined,
): TFacilityIdsByType {
  const buckets = createEmptyFacilityIdBuckets();
  facilities?.forEach((relation) => {
    const { type, facility_id } = relation || {};
    const id = typeof facility_id === 'string' ? facility_id : facility_id?.id;
    if (type && id && buckets[type]) {
      buckets[type].add(id);
    }
  });
  return buckets;
}

/**
 * Returns the user's home facility relation.
 *
 * "Home" (domain/UI term), `EUserFacilityRelationType.ADMINISTRATE`
 * (enum member), and `'default'` (the enum's underlying string value,
 * stored in the DB) all refer to the same concept.
 */
export function getHomeFacility(
  user: TUserWithFacilities | undefined,
): TUserFacilityRelation | undefined {
  return user?.facilities?.find((f) => f?.type === EUserFacilityRelationType.ADMINISTRATE);
}

export function isCoordinatingUser(user: TUserWithFacilities | undefined): boolean {
  return !!user?.facilities?.some((f) => f?.type === EUserFacilityRelationType.COORDINATE);
}

/**
 * Resolves the distinct facility ids reachable through the given planning groups.
 */
export function getFacilityIdsForPlanningGroups(
  planningGroups: ReadonlyArray<TPlanningGroup> | undefined,
  pgIds: ReadonlyArray<string> | undefined,
): TFacilityId[] {
  const wanted = new Set(pgIds);
  return [
    ...new Set(
      (planningGroups ?? [])
        .filter((pg) => wanted.has(pg.id))
        .flatMap((pg) => pg.facilities.map((f) => f.id)),
    ),
  ];
}
